import { useMediaQuery } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { getWidgetTranslationData } from '../../../App.util';
import { useThemeContext } from '../../../global-state/themeContext';
import { Icons, Typography } from '../../../stories/atoms';
import {
  ColumnFlexContainer,
  ContainerV2,
  RowFlexContainer,
} from '../../../stories/atoms/containerV2/ContainerV2';
import { muiTheme } from '../../../theme';
import { homeTranslations } from '../../../translations';
import { AreaWidgetType } from '../../areaOverview/util';
import { AreaWidgetsCategory, WidgetsCategory, WidgetType } from '../../home/types';
import { StyledIcon } from '../WidgetAccessControlPage.styles';
import { WidgetSwitch } from '../widgetSwitch/WidgetSwitch';

export const getWidgetCategoryIcon = (widgetsCategory: WidgetsCategory | AreaWidgetsCategory): Icons | null => {
  switch (widgetsCategory) {
    case WidgetsCategory.HIGHLIGHTED:
      return Icons.STAR;
    case WidgetsCategory.CUSTOMEREXPERIENCE:
      return Icons.PROFILE;
    case WidgetsCategory.CUSTOMERBASEVALUE:
      return Icons.CUSTOMERVALUE;
    case WidgetsCategory.SALESEFFICIENCY:
      return Icons.SALESEFFICIENCY;
    case WidgetsCategory.ALL:
      return Icons.ALLWIDGETS;
    case AreaWidgetsCategory.OVERVIEW:
      return Icons.MAP;
    default:
      return null;
  }
};

const highlightedWidgets: WidgetType[] = [
  WidgetType.NewMembers,
  WidgetType.ConversionRate,
  WidgetType.HourlyTurnover,
  WidgetType.ShareOfReceipts,
  WidgetType.QueueLengthWidget,
  WidgetType.SCOShareOfReceipts,
  WidgetType.CustomerEngagmentWidget,
  WidgetType.RfidSalesRetagging,
];

type WidgetCategoryProps = {
  category: WidgetsCategory | AreaWidgetsCategory;
  isSwitchDisabled: boolean;
  widgetTypes: WidgetType[] | AreaWidgetType[];
  onSwitchToggle: (widgetType: WidgetType | AreaWidgetType) => void;
  getIsActiveFromRoleMatrix: (widgetType: WidgetType | AreaWidgetType) => boolean;
  handleGetIsPermissionBlacklisted: (widgetType: WidgetType | AreaWidgetType) => boolean;
};

export const WidgetCategory: FC<WidgetCategoryProps> = ({
  category,
  isSwitchDisabled,
  widgetTypes,
  onSwitchToggle,
  getIsActiveFromRoleMatrix,
  handleGetIsPermissionBlacklisted,
}) => {
  const { t } = useTranslation();
  const isMobileTablet = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('mobile'));

  const { mode } = useThemeContext();
  const { widgetCategory: widgetCategoryTranslations } = homeTranslations;

  const icon = getWidgetCategoryIcon(category);

  return (
    <ContainerV2
      key={category}
      display="flex"
      flexDirection={isMobileTablet ? 'column' : 'row'}
      padding={[4, 5]}
      backgroundColor={mode === 'dark' ? 'backgroundPaperDark' : 'white'}
      hasBorder
      borderRadius={[5]}
    >
      <RowFlexContainer height={'fit-content'} flex={1} alignItems="center" gap={[2]} margin={[0, 0, 4]}>
        {icon && <StyledIcon icon={icon} size="small" />}

        <Typography type="h4" overflow={'visible'}>
          {t(widgetCategoryTranslations[category].key, widgetCategoryTranslations[category].defaultValue)}
        </Typography>
      </RowFlexContainer>

      <ColumnFlexContainer flex={2} maxHeight={isMobile ? 'none' : [60]} flexWrap={isMobile ? 'nowrap' : 'wrap'}>
        {widgetTypes.map((widgetType) => {
          const translationData = getWidgetTranslationData(widgetType);
          const isActive = !handleGetIsPermissionBlacklisted(widgetType);

          const isPermissionDisabled = !getIsActiveFromRoleMatrix(widgetType);
          const isSwitchActive = isActive && !isPermissionDisabled;

          if (!translationData) return null;

          return (
            <WidgetSwitch
              key={widgetType}
              shouldShowHighlightIcon={highlightedWidgets.includes(widgetType as WidgetType)}
              widgetType={widgetType}
              isPermissionDisabled={isPermissionDisabled}
              isSwitchActive={isSwitchActive}
              translationData={translationData}
              isSwitchDisabled={isSwitchDisabled}
              onSwitchToggle={onSwitchToggle}
            />
          );
        })}
      </ColumnFlexContainer>
    </ContainerV2>
  );
};
